import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FlightInfo } from './types';
import { cn } from '@/lib/utils';
import { 
  Plane, 
  Calendar, 
  Clock, 
  ChevronDown, 
  ChevronRight, 
  Link2Off,
  AlignLeft
} from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatFlightDateTime } from '@/lib/date-utils';

// Componentes estilizados localmente
const StyledLabel = ({ 
  children, 
  htmlFor, 
  className, 
  icon: Icon,
  ...props 
}: React.ComponentProps<typeof Label> & { icon?: React.ElementType }) => (
  <Label 
    htmlFor={htmlFor}
    className={cn("border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left w-fit text-sm font-medium text-gray-700", className)}
    {...props}
  >
    {Icon && <Icon className="h-3.5 w-3.5 text-voah-coral flex-shrink-0" />}
    <span className="whitespace-nowrap">{children}</span>
  </Label>
);

const FormInput = ({ className, ...props }: React.ComponentProps<typeof Input>) => (
  <Input
    className={cn("text-sm text-gray-600 font-normal", className)}
    {...props}
  />
);

interface FlightEventItemProps {
  flight: FlightInfo;
  eventType: 'departure' | 'arrival';
  index: number;
  pageId: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  updateFlight: (pageId: string, flightId: string, field: keyof FlightInfo, value: any) => void;
  deleteFlight: (pageId: string, flightId: string) => void;
}

export function FlightEventItem({
  flight,
  eventType,
  index,
  pageId,
  isCollapsed,
  onToggleCollapse,
  updateFlight,
  deleteFlight
}: FlightEventItemProps) {
  const isDeparture = eventType === 'departure';
  const isArrival = eventType === 'arrival';
  
  // Check for connections
  const hasConnections = flight.connections && flight.connections.length > 0;
  const lastConnection = hasConnections ? flight.connections[flight.connections.length - 1] : null;
  
  const title = isDeparture ? 'Partida' : 'Chegada';
  
  // Use appropriate flight info based on event type and connections
  const date = isDeparture 
    ? flight.departureDateTime 
    : (isArrival && lastConnection) 
      ? lastConnection.arrivalDateTime 
      : flight.arrivalDateTime;
  
  const airport = isDeparture 
    ? flight.departureAirport 
    : (isArrival && lastConnection) 
      ? lastConnection.arrivalAirport 
      : flight.arrivalAirport;
  
  const notes = isDeparture ? flight.departureNotes : flight.arrivalNotes;
  
  // Get airline and flight number info
  const airline = isDeparture 
    ? flight.airline 
    : (isArrival && lastConnection) 
      ? lastConnection.airline 
      : flight.airline;
      
  const flightNumber = isDeparture 
    ? flight.flightNumber 
    : (isArrival && lastConnection) 
      ? lastConnection.flightNumber 
      : flight.flightNumber;
  
  const borderColor = isDeparture ? 'border-l-green-500' : 'border-l-red-500';
  const icon = <Plane className={`h-4 w-4 ${isDeparture ? "text-green-500" : "text-red-500"}`} />;
  
  // Função para atualizar campos específicos do evento
  const updateEventField = (field: string, value: any) => {
    if (isDeparture) {
      if (field === 'date') {
        const [existingDate, existingTime] = flight.departureDateTime ? flight.departureDateTime.split('T') : [null, null];
        const newDateTime = value ? (existingTime ? `${value}T${existingTime}` : `${value}T00:00:00`) : null;
        updateFlight(pageId, flight.id, 'departureDateTime', newDateTime);
      }
      if (field === 'notes') updateFlight(pageId, flight.id, 'departureNotes', value);
    } else {
      if (field === 'date') {
        // For arrival with connections, we cannot directly edit the date here
        // as it would be stored in the connections array
        if (isArrival && lastConnection) {
          // Show a message or handle this differently if needed
          console.warn('Editing arrival date of a flight with connections is not directly supported');
          return;
        }
        
        const [existingDate, existingTime] = flight.arrivalDateTime ? flight.arrivalDateTime.split('T') : [null, null];
        const newDateTime = value ? (existingTime ? `${value}T${existingTime}` : `${value}T00:00:00`) : null;
        updateFlight(pageId, flight.id, 'arrivalDateTime', newDateTime);
      }
      if (field === 'notes') updateFlight(pageId, flight.id, 'arrivalNotes', value);
    }
  };
  
  // Extrair a data do formato ISO
  const extractDate = (isoString?: string): string => {
    if (!isoString) return '';
    // Use formato que extrai apenas a parte da data sem timezone
    return isoString.split('T')[0];
  };
  
  return (
    <Card className={cn(
      "border shadow-sm transition-all border-l-4",
      borderColor,
      isCollapsed && "border-transparent",
      isCollapsed ? borderColor : ""
    )}>
      <div 
        className={cn(
          "cursor-pointer",
          isCollapsed ? "h-9" : ""
        )}
        onClick={onToggleCollapse}
      >
        <div className="flex items-center justify-between w-full h-9">
          <div className="flex items-center gap-2 px-3 overflow-hidden">
            {icon}
            <h3 className="font-medium text-gray-700 whitespace-nowrap overflow-hidden text-ellipsis">
              {title}: {airline || ''} {flightNumber || ''}
              {isArrival && hasConnections && <span className="text-xs ml-1">(último voo)</span>}
            </h3>
          </div>
          
          <div className="flex items-center h-full">
            <Button 
              type="button"
              variant="ghost" 
              size="sm"
              className="h-9 w-8 p-0 rounded-none text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                deleteFlight(pageId, flight.id);
              }}
              title="Desativar modo de eventos separados"
            >
              <Link2Off className="h-4 w-4" />
            </Button>
            
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 w-8 p-0 rounded-none"
            >
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </Button>
          </div>
        </div>
      </div>
      
      {!isCollapsed && (
        <CardContent className="p-3">
          <div className="space-y-3">
            <div className="space-y-1">
              <StyledLabel icon={Calendar} htmlFor={`${eventType}-date-${flight.id}`}>Data</StyledLabel>
              <FormInput
                id={`${eventType}-date-${flight.id}`}
                value={extractDate(date) || ''}
                type="date"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateEventField('date', e.target.value)}
                placeholder="Selecione uma data"
                className={!date ? "border-amber-300" : ""}
                disabled={isArrival && hasConnections}
              />
              {!date && (
                <p className="text-xs text-amber-600 mt-1">Por favor, defina uma data para este evento</p>
              )}
              {isArrival && hasConnections && (
                <p className="text-xs text-gray-500 mt-1">A data da chegada é definida pelo último voo da conexão</p>
              )}
            </div>
            
            <div className="space-y-1">
              <StyledLabel icon={AlignLeft} htmlFor={`${eventType}-notes-${flight.id}`}>Observações</StyledLabel>
              <Textarea
                id={`${eventType}-notes-${flight.id}`}
                value={notes || ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => updateEventField('notes', e.target.value)}
                rows={2}
                className="resize-none text-sm"
                placeholder={`Observações sobre a ${title.toLowerCase()}...`}
              />
            </div>
            
            <div className="flex items-center space-x-2 pt-2 mt-1 text-sm text-gray-500">
              <Plane className="h-4 w-4" />
              <span>
                {isDeparture ? 'Partida' : 'Chegada'} do voo: {airline || ''} {flightNumber || ''}
                {airport ? ` - ${airport}` : ''}
              </span>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
} 